import { action, computed, makeObservable, observable } from 'mobx'
import { dependsOn, isPending, trackedAction } from 'mobx-async'

import { RequestStatus } from '@avantstay/arriere-clients/dist/arriereBackoffice'
import { Alert, pluralize } from '@avantstay/backoffice-core'

import { AdjustmentItemProps } from '@new/domains/bookings/screens/bookingAdjustmentRequest/BookingAdjustmentRequest.types'

import {
  approveRequests,
  cancelAdjustment,
  getAdjustmentRequestsToApprove,
  getAdjustmentRequestsToProcess,
  getApprovalRequirementsPreview,
  getBookingAdjustmentReasons,
  getBookingAdjustmentRecommendedBaseAmount,
  getBookingAdjustmentRecommendedTotalAmount,
  getRefundTargets,
  processManualRequests,
  refundPaymentSource,
  rejectRequests,
  requestAdjustment,
  updateRequest,
} from '@arriere/modules/bookingAdjustment'
import type {
  AdjustmentPossibilityProps,
  AdjustmentRequestReasons,
  AdjustmentRequestsToApprove,
  AdjustmentRequestsToProcess,
  ApproveBookingRequestsProps,
  CancelAdjustmentProps,
  PreviewApprovalRequirements,
  ProcessRequestProps,
  RecommendedAmountProps,
  RefundPaymentSourceProps,
  RefundTargets,
  RejectRequestProps,
  RequestAdjustmentProps,
  UpdateAdjustmentRequestProps,
} from '@arriere/types/BookingAdjustment.types'
import { trackError, trackEvent } from '@tracking/customEventReport'

import { BookingFolioStore } from './BookingFolioStore'
import { BookingStore } from './BookingStore'

class _BookingAdjustmentStore {
  bookingAdjustmentReasons?: AdjustmentRequestReasons['requestReasons']

  approvalRequirementsPreview?: PreviewApprovalRequirements[]

  adjustmentItemsToReview?: AdjustmentRequestsToApprove[]

  adjustmentItemToModify?: AdjustmentItemProps

  constructor() {
    makeObservable(this, {
      bookingAdjustmentReasons: observable,
      approvalRequirementsPreview: observable,
      adjustmentItemsToReview: observable,
      adjustmentItemToModify: observable,
      adjustmentRequestsToApprove: computed,
      adjustmentRequestsToProcess: computed,
      isLoadingReviewRequest: computed,
      refundTargets: computed,
      getReasonsForAdjustment: action.bound,
      getPreviewApprovalRequirements: action.bound,
      setAdjustmentItemToModify: action.bound,
    })

    this.refundPaymentMethods = trackedAction<any>(this.refundPaymentMethods.bind(this))
    this.processBookingRequests = trackedAction<any>(this.processBookingRequests.bind(this))
    this.rejectBookingRequests = trackedAction<any>(this.rejectBookingRequests.bind(this))
    this.approveBookingRequests = trackedAction<any>(this.approveBookingRequests.bind(this))
    this.cancelBookingAdjustment = trackedAction<any>(this.cancelBookingAdjustment.bind(this))
    this.updateBookingAdjustmentRequest = trackedAction<any>(
      this.updateBookingAdjustmentRequest.bind(this),
    )
    this.requestBookingAdjustment = trackedAction<any>(this.requestBookingAdjustment.bind(this))
    this.getBookingAdjustmentRecommendedAmount = trackedAction<any>(
      this.getBookingAdjustmentRecommendedAmount.bind(this),
    )
    this.getBookingAdjustmentAmountCalculation = trackedAction<any>(
      this.getBookingAdjustmentAmountCalculation.bind(this),
    )
    this.getBookingAdjustmentRequestsToApprove = trackedAction<any>(
      this.getBookingAdjustmentRequestsToApprove.bind(this),
    )
  }

  get adjustmentRequestsToApprove(): Promise<AdjustmentRequestsToApprove[]> {
    dependsOn(this.rejectBookingRequests, this.approveBookingRequests)

    const bookingId = BookingStore.bookingCache?.id

    return bookingId
      ? getAdjustmentRequestsToApprove(bookingId)
      : new Promise(resolve => resolve([]))
  }

  get adjustmentRequestsToProcess(): Promise<AdjustmentRequestsToProcess[]> {
    dependsOn(
      this.requestBookingAdjustment,
      this.processBookingRequests,
      this.rejectBookingRequests,
      this.approveBookingRequests,
    )

    const bookingId = BookingStore.bookingCache?.id

    return bookingId
      ? getAdjustmentRequestsToProcess(bookingId)
      : new Promise(resolve => resolve([]))
  }

  get isLoadingReviewRequest() {
    dependsOn(this.rejectBookingRequests, this.approveBookingRequests)

    return isPending(this.approveBookingRequests) || isPending(this.rejectBookingRequests)
  }

  get refundTargets(): Promise<RefundTargets> {
    dependsOn(BookingFolioStore.bookingBalanceSummary)

    const bookingId = BookingStore.bookingCache?.id

    return bookingId ? getRefundTargets(bookingId) : new Promise(resolve => resolve([]))
  }

  async getReasonsForAdjustment() {
    if (!this.bookingAdjustmentReasons) {
      const bookingAdjustmentReasons = await getBookingAdjustmentReasons()

      this.bookingAdjustmentReasons = bookingAdjustmentReasons.requestReasons.filter(reasons =>
        reasons.group.category.name.includes('Booking Ad Hoc Refund'),
      )
    }
  }

  async getPreviewApprovalRequirements({ bookingId, inputs }: AdjustmentPossibilityProps) {
    const previewApprovalRequirements = await getApprovalRequirementsPreview({
      bookingId,
      inputs,
    })

    this.approvalRequirementsPreview = previewApprovalRequirements
  }

  setAdjustmentItemToModify = (item?: AdjustmentItemProps) => {
    this.adjustmentItemToModify = item
  }

  async getBookingAdjustmentRequestsToApprove(bookingId: string) {
    const requestsToApprove = await getAdjustmentRequestsToApprove(bookingId)

    this.adjustmentItemsToReview = requestsToApprove.filter(
      requests => requests.status === RequestStatus.requested,
    )
  }

  async getBookingAdjustmentAmountCalculation(bookingId: string, reasonId: number) {
    return getBookingAdjustmentRecommendedBaseAmount(bookingId, reasonId)
  }

  async getBookingAdjustmentRecommendedAmount({
    bookingId,
    reasonId,
    baseAmount,
    quantity,
  }: RecommendedAmountProps) {
    return getBookingAdjustmentRecommendedTotalAmount({ bookingId, reasonId, baseAmount, quantity })
  }

  async requestBookingAdjustment({
    bookingId,
    requests,
    date,
    onSuccess,
  }: RequestAdjustmentProps & { onSuccess: VoidFunction }) {
    return requestAdjustment({ bookingId, requests, date })
      .then(({ data, errors }) => {
        if (data) {
          Alert.Success('Booking adjustment requested successfully')
          trackEvent('Booking adjustment requested successfully', {
            bookingId,
            requests,
          })
          onSuccess()
          this.getBookingAdjustmentRequestsToApprove(bookingId)
        }

        if (errors && errors[0]?.message) {
          throw new Error(
            `Oops! Something went wrong: ${errors[1] ? errors[1]?.message : errors[0]?.message}.`,
          )
        }
      })
      .catch(error => {
        trackError('Booking adjustment request: error trying to request adjustment', {
          error,
          bookingId,
          requests,
        })
        Alert.Error(error)
      })
  }

  async updateBookingAdjustmentRequest({
    bookingId,
    requests,
    cancelRequestIds,
    cancelNote,
    onSuccess,
  }: UpdateAdjustmentRequestProps & { onSuccess: VoidFunction }) {
    return updateRequest({ bookingId, requests, cancelRequestIds, cancelNote })
      .then(({ data, errors }) => {
        if (data) {
          Alert.Success('Booking adjustment updated successfully')
          trackEvent('Booking adjustment updated successfully', {
            bookingId,
            requests,
          })
          onSuccess()
          this.getBookingAdjustmentRequestsToApprove(bookingId)
        }

        if (errors && errors[0]?.message) {
          throw new Error(
            `Oops! Something went wrong: ${errors[1] ? errors[1]?.message : errors[0]?.message}.`,
          )
        }
      })
      .catch(error => {
        trackError('Booking adjustment: error trying to update adjustment', {
          error,
          bookingId,
          requests,
        })
        Alert.Error(`Booking adjustment: ${error}`)
      })
  }

  async cancelBookingAdjustment({
    requestIds,
    note,
    onSuccess,
  }: CancelAdjustmentProps & { onSuccess?: VoidFunction }) {
    return cancelAdjustment({ requestIds, note })
      .then(({ data, errors }) => {
        if (data) {
          Alert.Success('Booking adjustment request cancelled successfully')
          trackEvent('Booking adjustment request cancelled successfully', {
            requestIds,
            note,
          })
          onSuccess?.()
        }

        if (errors && errors[0]?.message) {
          throw new Error(
            `Oops! Something went wrong: ${errors[1] ? errors[1]?.message : errors[0]?.message}.`,
          )
        }
      })
      .catch(error => {
        trackError('Booking adjustment cancelled: error trying to cancel adjustment', {
          error,
          requestIds,
          note,
        })
        Alert.Error(error)
      })
  }

  async approveBookingRequests({
    bookingId,
    requests,
    onSuccess,
  }: ApproveBookingRequestsProps & { onSuccess?: VoidFunction }) {
    return approveRequests({ bookingId, requests })
      .then(({ data, errors }) => {
        if (data?.approveRequests) {
          trackEvent('Booking adjustment approved!', {
            requests,
          })

          Alert.Success(
            `Approved ${pluralize(requests.length, 'adjustments', 'adjustment')} successfully`,
          )

          onSuccess?.()
        }

        if (errors && errors[0]?.message) {
          throw new Error(
            `Oops! Something went wrong: ${errors[1] ? errors[1]?.message : errors[0]?.message}.`,
          )
        }
      })
      .catch(error => {
        trackError('Booking risk verification: error trying to update booking', {
          requests,
        })
        Alert.Error(error)
      })
  }

  async rejectBookingRequests({
    onSuccess,
    note,
    requestIds,
  }: RejectRequestProps & { onSuccess?: VoidFunction }) {
    return rejectRequests({ note, requestIds })
      .then(({ data, errors }) => {
        if (data?.rejectRequests) {
          Alert.Success(
            `Rejected ${pluralize(requestIds.length, 'adjustments', 'adjustment')} successfully`,
          )
          trackEvent('Booking adjustment rejected!', {
            requestIds,
          })

          onSuccess?.()
        }

        if (errors && errors[0]?.message) {
          throw new Error(
            `Oops! Something went wrong: ${errors[1] ? errors[1]?.message : errors[0]?.message}.`,
          )
        }
      })
      .catch(error => {
        trackError('Booking risk verification: error trying to update booking', {
          requestIds,
        })
        Alert.Error(error)
      })
  }

  async processBookingRequests({
    onSuccess,
    requests,
  }: ProcessRequestProps & { onSuccess?: VoidFunction }) {
    return processManualRequests({ requests })
      .then(({ data, errors }) => {
        if (data?.processManualRequests) {
          trackEvent('Booking adjustment processed!', {
            requests,
          })

          Alert.Success(`Processed adjustment successfully`)

          onSuccess?.()
        }

        if (errors && errors[0]?.message) {
          throw new Error(
            `Oops! Something went wrong: ${errors[1] ? errors[1]?.message : errors[0]?.message}.`,
          )
        }
      })
      .catch(error => {
        trackError('Booking risk verification: error trying to update booking', {
          requests,
        })
        Alert.Error(error)
      })
  }

  async refundPaymentMethods({
    bookingId,
    methods,
    date,
    onSuccess,
  }: RefundPaymentSourceProps & { onSuccess?: VoidFunction }) {
    return refundPaymentSource({ bookingId, methods, date })
      .then(({ data, errors }) => {
        if (data?.refundPaymentSource) {
          trackEvent('Refund processed successfully', {
            bookingId,
            methods,
            date,
          })

          Alert.Success('Refund processed successfully')
          onSuccess?.()
        }

        if (errors?.length) {
          throw new Error(`Oops! Something went wrong: ${errors[errors?.length - 1]?.message}.`)
        }
      })
      .catch(error => {
        trackError('Refund: error trying to process refund', {
          bookingId,
          methods,
        })
        Alert.Error(error)
      })
  }
}

export const BookingAdjustmentStore = new _BookingAdjustmentStore()
